import React, { useEffect, useState } from "react";

import { Link, makeStyles, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import CreateIcon from "@material-ui/icons/Create";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import moment from "moment";

import Container from "../../components/Container";
import LightPurpleContainer from "../../components/LightPurpleContainer";
import Title from "../../components/Title";
import NurseGallery from "../NurseHomepage/components/NurseGallery/NurseGallery";
import Legend from "./components/Legend";
import Button from "../../components/Button";
import ConfirmationModal from "../../components/ConfirmationModal";
import DeclineRequestForm from "./components/DeclineRequestForm";
import { approveSurvey, getOptionalQuestions, getRequestByHospital } from "../../api/hospital";
import { HospitalSurvey, ReviewRequest } from "../../types/hospital";
import { blankReviewRequest } from "../../constants/objects";
import SurveyTable from "../../components/SurveyTable";
import ModalDialog from "../../components/Form/ModalDialog";
import HospitalSurveyForm from "../../components/Form/HospitalSurveyForm";
import { Image } from "../NurseHomepage/components/NurseGallery/types";
import theme from "../../constants/theme";
import Loading from "../../components/Loading";
import { OptionalQuestion } from "../../components/Form/types";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(5, 0),
    backgroundColor: "#fff",
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
    },
  },
  mobile: {
    [theme.breakpoints.only("xs")]: {
      fontSize: 20,
      marginLeft: theme.spacing(1),
    },
  },
  grow: { flexGrow: 1 },
  editIcon: {
    fontSize: theme.spacing(2.2),
    color: theme.palette.primary.main,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.only("xs")]: {
      width: "100%",
    },
  },
  noData: {
    marginBottom: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
  },
  title: {
    color: theme.palette.primary.main,
    display: "inline-block",
    fontSize: theme.spacing(2.5),
    fontWeight: "bold",
    margin: theme.spacing(1.8),
  },
  responsiblePerson: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.secondary.dark}`,
    background: theme.palette.grey[100],
    borderRadius: 4,
    color: theme.palette.grey[600],
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.only("xs")]: {
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(0),
    },
  },
  back: {
    display: "flex",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    cursor: "pointer",
  },
  backIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
}));

type RodaRequestProps = {
  hospitalId: number;
  hospitalName: string;
  redirectToRequestsList: () => void;
};

export default function RodaRequest({
  hospitalId,
  hospitalName,
  redirectToRequestsList,
}: RodaRequestProps) {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [declineModalOpen, setDeclineModalOpen] = useState<boolean>(false);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
  const [request, setRequest] = useState<ReviewRequest>(blankReviewRequest);
  const [optionalQuestions, setOptionalQuestions] = useState<OptionalQuestion[]>([]);

  useEffect(() => {
    async function fetchData() {
      const response = await getRequestByHospital(hospitalId);
      const optionalQuestionList = await getOptionalQuestions();
      await setRequest(response.data);
      setOptionalQuestions(optionalQuestionList.data);
      setLoading(false);
    }

    fetchData();
    window.scrollTo(0, 0);
  }, []);

  function handleSurveyDataChange(surveyData: HospitalSurvey) {
    setRequest({ ...request, surveyDTO: surveyData });
  }

  function handleGalleryChange(gallery: Image[]) {
    setRequest({ ...request, galleryDTO: { gallery: gallery } });
  }

  const handleSurveySubmit = async () => {
    await approveSurvey(hospitalId);
    setRequest({ ...request, surveyDTO: undefined });
    setApproveModalOpen(false);
  };

  return (
    <>
      <Container>
        <Link className={classes.back} onClick={redirectToRequestsList}>
          <ArrowBackIcon className={classes.backIcon} />
          <Typography color="primary">Popis zahtjeva</Typography>
        </Link>
        <LightPurpleContainer>
          <div className={classes.container}>
            <Typography
              variant="h4"
              style={{ marginRight: 20 }}
              className={classes.mobile}
            >
              Zahtjev za bolnicu:
            </Typography>
            <Typography
              variant="h4"
              color="secondary"
              className={classes.mobile}
            >
              {hospitalName}
            </Typography>
          </div>
        </LightPurpleContainer>
        {loading && <Loading />}
        {!request.galleryDTO?.gallery.length && !request.surveyDTO && (
          <div className={classes.container}>
            {/*TODO change ugly inline style*/}
            <Typography
              variant="h4"
              style={{ marginRight: 20, marginTop: 50 }}
              className={classes.noData}
            >
              Nema više zahtjeva za ovu bolnicu.
              <Button
                style={{ marginTop: 50 }}
                onClick={redirectToRequestsList}
              >
                {" "}
                Povratak na listu zahtjeva{" "}
              </Button>
            </Typography>
          </div>
        )}
        {request.surveyDTO && (
          <>
            <div
              className={classes.container}
              style={{ padding: theme.spacing(2, 0) }}
            >
              <Typography variant="body1" className={classes.responsiblePerson}>
                Zadnje ažurirano:{" "}
                {request.surveyDTO.userName +
                  " " +
                  request.surveyDTO.userSurname}
                ,{" "}
                {moment(request.surveyDTO.lastUpdate).format(
                  "DD.MM.YYYY. u HH:mm"
                )}
              </Typography>

              <div className={classes.grow} />
              <Button
                type="button"
                startIcon={<CreateIcon />}
                onClick={() => setModalOpen(true)}
              >
                Uredi
              </Button>
            </div>
            <SurveyTable surveyData={request.surveyDTO} optionalQuestions={optionalQuestions}/>
            <div className={classes.buttonContainer}>
              <Button
                onClick={() => setDeclineModalOpen(true)}
                startIcon={<DeleteIcon />}
                variant="outlined"
                style={{ marginRight: 16 }}
              >
                Odbij
              </Button>
              <Button
                onClick={() => setApproveModalOpen(true)}
                startIcon={<CheckRoundedIcon />}
              >
                Odobri
              </Button>
            </div>

            <ModalDialog
              modalOpen={modalOpen}
              handleClose={() => setModalOpen(false)}
              maxWidth="lg"
              icon={<CreateIcon className={classes.editIcon} />}
              title={<Typography className={classes.title}>Uredi</Typography>}
            >
              <HospitalSurveyForm
                hospitalId={hospitalId}
                handleClose={() => setModalOpen(false)}
                surveyData={request.surveyDTO}
                removeData={() =>
                  setRequest({ ...request, surveyDTO: undefined })
                }
                handleDataChange={handleSurveyDataChange}
                optionalQuestions={optionalQuestions}
                setApproveModalOpen={() => setApproveModalOpen(true)}
              />
            </ModalDialog>
            <ModalDialog
              modalOpen={declineModalOpen}
              handleClose={() => setDeclineModalOpen(false)}
              maxWidth="md"
              title={
                <Typography className={classes.title}>
                  Jeste li sigurni?
                </Typography>
              }
              icon={<DeleteIcon className={classes.editIcon} />}
            >
              <DeclineRequestForm
                hospitalId={hospitalId}
                handleDeny={() => {
                  setDeclineModalOpen(false);
                  setRequest({ ...request, surveyDTO: undefined });
                }}
                handleClose={() => {
                  setDeclineModalOpen(false);
                }}
              />
            </ModalDialog>
          </>
        )}
        {!!request.galleryDTO?.gallery.length && (
          <>
            <Title label="Galerija" />
            <Legend />
            <NurseGallery
              galleryProp={request.galleryDTO?.gallery}
              isReview={true}
              handleGalleryChange={handleGalleryChange}
            />
          </>
        )}
      </Container>

      <ConfirmationModal
        modalOpen={approveModalOpen}
        handleAction={handleSurveySubmit}
        handleClose={() => setApproveModalOpen(false)}
        modalText={"Jeste li sigurni da želite odobriti podatke o ustanovi?"}
        title={"Jeste li sigurni?"}
      />
    </>
  );
}
